import Modifier from 'ember-modifier';
import { registerDestructor } from '@ember/destroyable';
import '../../config/echarts_theme';

// Import the echarts core module, which provides the necessary interfaces for using echarts.
import * as echarts from 'echarts/core';
import { TooltipComponent, LegendComponent } from 'echarts/components';
import { PieChart, SunburstChart } from 'echarts/charts';
import { LabelLayout } from 'echarts/features';
import { CanvasRenderer } from 'echarts/renderers';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

echarts.use([
  SunburstChart,
  TooltipComponent,
  LegendComponent,
  PieChart,
  CanvasRenderer,
  LabelLayout,
]);

function cleanup(instance) {
  instance?.chart?.dispose?.();
}

export default class InitiativeTicketStatsModifier extends Modifier {
  @service intl;

  constructor(owner, args) {
    super(owner, args);
    registerDestructor(this, cleanup);
  }

  @tracked initiative;
  @tracked workstreams = [];

  async modify(element, [initiative], options) {
    if (!initiative) return;
    this.initiative = initiative;
    this.options = options;
    let promises = [];
    const workstreams =
      (await this.initiative?.hasMany?.('workstreams')?.load?.()) ?? [];
    this.workstreams = workstreams;
    workstreams.forEach((workstream) => {
      promises.push(workstream.hasMany('tfos').load());
    });
    await Promise.all(promises);
    await this.setupChart(element);
  }

  async setupChart(element) {
    if (this.chart != undefined) return;
    this.chart = echarts.init(element, 'producthub', {
      height: this.options.height ?? 256,
      width: this.options.width ?? 512,
    });

    const options = {
      ...this.chartOptions,
      series: this.series,
    };

    this.chart.setOption(options);
  }

  get chartOptions() {
    const options = {
      tooltip: {
        trigger: 'item',
      },
    };

    if (this.options.formatter) {
      options.tooltip.formatter = this.options.formatter;
    }

    return options;
  }

  get chartData() {
    const data = this.workstreams.map((workstream) => {
      return {
        name: workstream.title,
        value: workstream.totalTickets,
        tooltip: {
          formatter: `{b}: {c} Total tickets`,
        },
        children: [
          {
            value: workstream.openTickets,
            name: 'Open',
            tooltip: {
              formatter: `{b}: {c} Tickets`,
            },
          },
          {
            value: workstream.closedTickets,
            name: 'Closed',
            tooltip: {
              formatter: `{b}: {c} Tickets`,
            },
          },
        ],
      };
    });

    if (this.initiative.unlistedTickets !== 0) {
      data.push({
        name: 'Unlisted',
        value: this.initiative.unlistedTickets,
        tooltip: {
          formatter: `{b}: {c} Total tickets`,
        },
        children: [
          {
            value: this.initiative.unlistedOpenTickets,
            name: 'Open',
            tooltip: {
              formatter: `{b}: {c} Tickets`,
            },
          },
          {
            value: this.initiative.unlistedClosedTickets,
            name: 'Closed',
            tooltip: {
              formatter: `{b}: {c} Tickets`,
            },
          },
        ],
      });
    }
    return data;
  }

  get series() {
    const series = {
      type: 'sunburst',
      radius: [0, '90%'],
      itemStyle: {
        borderRadius: 7,
        borderWidth: 2,
      },
      label: {
        show: true,
        rotate: 'radial',
        fontWeight: 'normal',
        // formatter: '{b}: {c} Tickets'
      },
      data: this.chartData,
    };

    return series;
  }
}
